import * as React from "react";
import * as _ from "lodash";
import { DayModifiers } from "react-day-picker";
import styled, { css } from "styled-components";

import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { IOrderStatus } from "src/core/orders/api/orders.api";
import { formatDate } from "src/library/utils";

interface DayProps {
  date: Date;
  modifiers?: DayModifiers;
  className?: string;
  details?: DayDetails[];
  loading: boolean;
  showMonth?: boolean;
  handleDayClick?: () => void;
}

export interface DayDetails {
  status: IOrderStatus;
  qty: number;
  id: number;
}

const today = new Date();

const Day: React.ReactFCWithChildren<DayProps> = props => {
  const isToday = props.date.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0);

  const getDayClass = React.useCallback(() => {
    if (props.details) {
      const hasError = _.filter(props.details, deet => deet.status.settings.group === "ERROR");

      if (hasError.length) {
        return "Day--error";
      }

      const hasDeliveryDue = _.filter(props.details, deet => deet.status.settings.group === "DUE");

      if (hasDeliveryDue.length) {
        return "Day--delivery-due";
      }

      const hasProvisional = _.filter(props.details, deet => deet.status.settings.group === "PROVISIONAL");

      if (hasProvisional.length) {
        return "Day--provisional";
      }

      return "";
    }

    return "Day--disabled";
  }, [ props.details ]);

  const dayClasses = _.compact([ props.className, getDayClass() ]);
  const dateOfMonth = props.date.getDate();

  return (
    <DayWrapper
      isToday={isToday}
      className={`${dayClasses.join(" ")} ${getDayClass()}`}
      onClick={props.handleDayClick}
    >
      <DateWrapper className="DateWrapper">
        <DateOfMonth
          weight={isToday ? "bold" : "normal"}
          color={isToday ? "primary" : "secondary"}
        >
          {`${dateOfMonth} ${dateOfMonth === 1 || props.showMonth ? formatDate(props.date, "MMM") : ""}`}
        </DateOfMonth>
      </DateWrapper>

      <DayDetail>
        {props.loading ? (
          <LoadingSpinner
            position="inline"
            type="circular"
            size={15}
            color="primaryLight"
          />
        ) : (
          props.details &&
          props.details.map((detail, i) => (
            <Detail
              key={`day-detail-${i}`}
              className={`detail-${detail.status.settings.group}`}
            >
              <TextTruncate
                weight="light"
                fontSize="sub"
              >
                {`${detail.qty} order ${detail.status.name.toLowerCase()}`}
              </TextTruncate>
            </Detail>
          ))
        )}
      </DayDetail>
    </DayWrapper>
  );
};

const DayWrapper = styled.div<{isToday: boolean}>`
  ${({ theme, isToday }) => css`
    display: flex;
    flex: 1 1 auto;
    text-align: left;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 80px;
    background: white;
    padding: ${theme.spacing.padding.xs};
    margin: 5px 2px;
    border-top: 2px solid ${isToday ? theme.colors.primaryDark : theme.colors.lightGrey};

    &.Day--hasInfo {
      cursor: pointer;
    }

    &:not(.Day--disabled):hover {
      transition: ease-in-out 0.2s all;
    }

    ${breakpoint("lg", theme)} {
      padding: ${theme.spacing.padding.md} ${theme.spacing.padding.xs};
      margin: 5px;
      min-height: 150px;
    }
  `}

  font-size: 15px;
`;

const DateWrapper = styled.div`
  display: flex;
  text-align: left;
  flex-direction: row;
`;

const DateOfMonth = styled(Text)`
  padding-right: 5px;
  font-size: 1.7rem;
`;

const DayDetail = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const Detail = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    margin: 5px 0;
    padding: 2px ${theme.spacing.padding.xs};
    border-radius: ${theme.borderRadius};

    &.detail-DRAFT {
      background: ${theme.colors.draftOrder};
    }

    &.detail-DUE {
      background: ${theme.colors.dueOrder};
    }

    &.detail-PROVISIONAL {
      background: ${theme.colors.provisionalOrder};
    }

    &.detail-CANCELLED {
      background: ${theme.colors.cancelledOrder};
    }

    &.detail-ERROR {
      background: ${theme.colors.errorOrder};
    }
  `}
`;

export default Day;
